import { supabase } from '../lib/supabase';
import { applyDomainFilterToQuery } from '../hooks/useDomainQuery';

export interface CourseReview {
  id: string;
  tenant_id: string;
  course_id: string;
  student_id: string;
  enrollment_id: string;
  rating: number;
  review_text: string | null;
  is_published: boolean;
  created_at: string;
  updated_at: string;
  student?: {
    id: string;
    name: string;
    avatar_url: string | null;
  };
}

/**
 * Récupère les avis publiés d'un cours avec pagination
 */
export async function getPublishedCourseReviews(
  tenantId: string, 
  courseId: string, 
  limit: number = 10, 
  offset: number = 0
): Promise<{ data: CourseReview[], count: number }> {
  try {
    let query = supabase
      .from('elearn_course_reviews')
      .select(`
        *,
        student:student_id(id, name:full_name, avatar_url)
      `, { count: 'exact' })
      .eq('course_id', courseId)
      .eq('is_published', true)
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1);
    
    query = applyDomainFilterToQuery(query);
    const { data, error, count } = await query;

    if (error) throw error;

    return { data: data || [], count: count || 0 };
  } catch (error) {
    console.error(`Erreur lors de la récupération des avis du cours ${courseId}:`, error);
    return { data: [], count: 0 };
  }
}

/**
 * Récupère tous les avis d'un cours (publiés et non publiés) pour l'administration
 */
export async function getAllCourseReviews(
  tenantId: string, 
  courseId: string
): Promise<CourseReview[]> {
  try {
    let query = supabase
      .from('elearn_course_reviews')
      .select(`
        *,
        student:student_id(id, name:full_name, avatar_url)
      `)
      .eq('course_id', courseId)
      .order('created_at', { ascending: false });
    
    query = applyDomainFilterToQuery(query);
    const { data, error } = await query;

    if (error) throw error;

    return data || [];
  } catch (error) {
    console.error(`Erreur lors de la récupération des avis du cours ${courseId}:`, error);
    return [];
  }
}

/**
 * Vérifie si un étudiant a déjà laissé un avis sur un cours
 */
export async function hasStudentReviewedCourse(
  tenantId: string,
  courseId: string,
  studentId: string
): Promise<boolean> {
  try {
    let query = supabase
      .from('elearn_course_reviews')
      .select('id')
      .eq('course_id', courseId)
      .eq('student_id', studentId);
    
    query = applyDomainFilterToQuery(query);
    const { data, error } = await query.single();

    if (error) return false;
    return !!data;
  } catch (error) {
    console.error(`Erreur lors de la vérification de l'avis de l'étudiant:`, error);
    return false;
  }
}

/**
 * Récupère l'avis d'un étudiant sur un cours
 */
export async function getStudentCourseReview(
  tenantId: string,
  courseId: string,
  studentId: string
): Promise<CourseReview | null> {
  try {
    let query = supabase
      .from('elearn_course_reviews')
      .select('*')
      .eq('course_id', courseId)
      .eq('student_id', studentId);
    
    query = applyDomainFilterToQuery(query);
    const { data, error } = await query.single();

    if (error) return null;
    return data;
  } catch (error) {
    console.error(`Erreur lors de la récupération de l'avis de l'étudiant:`, error);
    return null;
  }
}

/**
 * Crée ou met à jour un avis sur un cours
 */
export async function createOrUpdateCourseReview(
  review: {
    tenant_id: string;
    course_id: string;
    student_id: string;
    enrollment_id: string;
    rating: number;
    review_text?: string;
    is_published?: boolean;
  }
): Promise<CourseReview | null> {
  try {
    // Récupérer les paramètres de domaine pour l'ajout automatique
    const { getDomainFilterParams } = await import('../hooks/useDomainQuery');
    const { domain } = getDomainFilterParams();

    // Vérifier si l'avis existe déjà
    const existingReview = await getStudentCourseReview(
      review.tenant_id,
      review.course_id,
      review.student_id
    );

    if (existingReview) {
      // Mettre à jour l'avis existant
      let updateQuery = supabase
        .from('elearn_course_reviews')
        .update({
          rating: review.rating,
          review_text: review.review_text,
          is_published: review.is_published !== undefined ? review.is_published : existingReview.is_published,
          updated_at: new Date().toISOString(),
          ...(domain && { domain })
        })
        .eq('id', existingReview.id);

      updateQuery = applyDomainFilterToQuery(updateQuery);
      const { data, error } = await updateQuery.select().single();

      if (error) throw error;
      
      // Le trigger SQL update_course_rating met à jour automatiquement les statistiques
      
      return data;
    } else {
      // Créer un nouvel avis
      const { data, error } = await supabase
        .from('elearn_course_reviews')
        .insert([{
          ...review,
          ...(domain && { domain }),
          is_published: review.is_published !== undefined ? review.is_published : false,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }])
        .select()
        .single();

      if (error) throw error;
      
      // Le trigger SQL update_course_rating met à jour automatiquement les statistiques
      
      return data;
    }
  } catch (error) {
    console.error(`Erreur lors de la création/mise à jour de l'avis:`, error);
    return null;
  }
}

/**
 * Supprime un avis sur un cours
 */
export async function deleteCourseReview(
  tenantId: string,
  reviewId: string
): Promise<boolean> {
  try {
    // Récupérer d'abord l'avis pour obtenir le course_id
    let reviewQuery = supabase
      .from('elearn_course_reviews')
      .select('course_id')
      .eq('id', reviewId);
    
    reviewQuery = applyDomainFilterToQuery(reviewQuery);
    const { data: review } = await reviewQuery.single();
    
    if (!review) return false;
    
    let deleteQuery = supabase
      .from('elearn_course_reviews')
      .delete()
      .eq('id', reviewId);
    
    deleteQuery = applyDomainFilterToQuery(deleteQuery);
    const { error } = await deleteQuery;

    if (error) throw error;
    
    // Le trigger SQL update_course_rating met à jour automatiquement les statistiques
    
    return true;
  } catch (error) {
    console.error(`Erreur lors de la suppression de l'avis:`, error);
    return false;
  }
}

/**
 * Modifie le statut de publication d'un avis
 */
export async function toggleReviewPublicationStatus(
  tenantId: string,
  reviewId: string,
  isPublished: boolean
): Promise<boolean> {
  try {
    // Récupérer d'abord l'avis pour obtenir le course_id
    let reviewQuery = supabase
      .from('elearn_course_reviews')
      .select('course_id')
      .eq('id', reviewId);
    
    reviewQuery = applyDomainFilterToQuery(reviewQuery);
    const { data: review } = await reviewQuery.single();
    
    if (!review) return false;
    
    let updateQuery = supabase
      .from('elearn_course_reviews')
      .update({ is_published: isPublished })
      .eq('id', reviewId);
    
    updateQuery = applyDomainFilterToQuery(updateQuery);
    const { error } = await updateQuery;

    if (error) throw error;
    
    // Le trigger SQL update_course_rating met à jour automatiquement les statistiques
    
    return true;
  } catch (error) {
    console.error(`Erreur lors de la modification du statut de publication:`, error);
    return false;
  }
}

/**
 * Note: La mise à jour des statistiques de notation est gérée automatiquement par
 * le trigger SQL 'update_course_rating' qui s'exécute après chaque INSERT, UPDATE
 * ou DELETE sur la table 'elearn_course_reviews'.
 */
